export enum UserRole {
  TRAVELER = 'traveler',
  GUIDE = 'guide',
  SUPER_ADMIN = 'super_admin',
}

export enum Permission {
  BROWSE_GUIDES = 'browse_guides',
  SEND_REQUESTS = 'send_requests',
  SUBMIT_REVIEWS = 'submit_reviews',
  RECEIVE_REQUESTS = 'receive_requests',
  MANAGE_GUIDE_PROFILE = 'manage_guide_profile',
  VIEW_GUIDE_DASHBOARD = 'view_guide_dashboard',
  FULL_ACCESS = 'full_access',
}

/**
 * An authentication method, not a kind of user. One account may hold several
 * at once — mirrors `server/src/shared/enums/index.ts`.
 */
export enum AuthProvider {
  GOOGLE = 'google',
  PHONE = 'phone',
  APPLE = 'apple',
  EMAIL = 'email',
}

/** Stable auth error codes. Branch on these, never on the message text. */
export enum AuthErrorCode {
  IDENTITY_CONFLICT = 'IDENTITY_CONFLICT',
  IDENTITY_ALREADY_LINKED = 'IDENTITY_ALREADY_LINKED',
  INVALID_PROVIDER_IDENTITY = 'INVALID_PROVIDER_IDENTITY',
  ACCOUNT_LINK_REQUIRES_VERIFICATION = 'ACCOUNT_LINK_REQUIRES_VERIFICATION',
  ACCOUNT_NOT_FOUND = 'ACCOUNT_NOT_FOUND',
  ACCOUNT_SUSPENDED = 'ACCOUNT_SUSPENDED',
}

/** One connected login method, as shown in Account & Security. */
export interface AuthIdentitySummary {
  provider: AuthProvider;
  /** Already masked by the server — never a raw phone or Google subject id. */
  label: string;
  linkedAt: string;
  lastUsedAt?: string;
}

/**
 * @deprecated Legacy single status. Mirrors the three lifecycle enums below
 * and is kept only so untouched legacy code keeps compiling. Read
 * `GuideProfileSummary.lifecycle` instead.
 */
export enum GuideStatus {
  DRAFT = 'draft',
  PROFILE_INCOMPLETE = 'profile_incomplete',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
}

/** Stage 1 - has this person passed background verification? */
export enum VerificationStatus {
  NOT_STARTED = 'not_started',
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  PASSED = 'passed',
  FAILED = 'failed',
}

/** Stage 2 - is the guide profile complete and approved? */
export enum ProfileStatus {
  NOT_STARTED = 'not_started',
  IN_PROGRESS = 'in_progress',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  CHANGES_REQUIRED = 'changes_required',
  APPROVED = 'approved',
}

/** Stage 3 - should this guide be visible and bookable? */
/**
 * Where an applicant sits in the admin onboarding pipeline.
 *
 * Mirrors `OnboardingStage` in
 * server/src/modules/guide-profile/guide-lifecycle.state-machine.ts, which is
 * authoritative. The server derives the stage and ships it per row — nothing
 * here re-implements the precedence rules.
 */
export enum OnboardingStage {
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
  BACKGROUND_CHECK = 'background_check',
  AWAITING_GUIDE = 'awaiting_guide',
  PROFILE_REVIEW = 'profile_review',
  READY_TO_PUBLISH = 'ready_to_publish',
  LIVE = 'live',
}

/** Whose court the ball is in. */
export type WaitingOn = 'admin' | 'springverify' | 'guide' | 'none';

/** How a guide's background verification is carried out. */
export enum VerificationProvider {
  MANUAL = 'manual',
  SPRING_VERIFY = 'spring_verify',
}

/** Self-reported guiding experience from the "Become a Guide" application. */
export enum GuideExperienceRange {
  NONE = 'none',
  ONE_TO_FIVE = '1_5',
  FIVE_TO_TEN = '5_10',
  TEN_TO_FIFTEEN = '10_15',
  FIFTEEN_TO_TWENTY = '15_20',
  TWENTY_PLUS = '20_plus',
}

export enum MarketplaceStatus {
  NOT_READY = 'not_ready',
  PENDING_APPROVAL = 'pending_approval',
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
}

export enum DocumentType {
  SELFIE = 'selfie',
  AADHAAR_FRONT = 'aadhaar_front',
  AADHAAR_BACK = 'aadhaar_back',
  CERTIFICATE = 'certificate',
  PROFILE_PHOTO = 'profile_photo',
}

export enum GuideVerificationAction {
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  SUSPENDED = 'suspended',
  INFO_REQUESTED = 'info_requested',
  IDENTITY_ACTIVATED = 'identity_activated',
  IDENTITY_SUSPENDED = 'identity_suspended',
  IDENTITY_REVOKED = 'identity_revoked',
}

export enum GuideIdentityStatus {
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  REVOKED = 'REVOKED',
}

export enum GuideRequestStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
  EXPIRED = 'expired',
}

export enum GuideAvailabilityStatus {
  AVAILABLE = 'available',
  BUSY = 'busy',
  VACATION = 'vacation',
}

export enum GuideAvailabilitySource {
  MANUAL = 'manual',
  BOOKING = 'booking',
}

export enum BookingPricingType {
  HOURLY = 'hourly',
  FULL_DAY = 'fullDay',
}

export enum BookingStatus {
  CREATED = 'CREATED',
  DRAFT = 'DRAFT',
  PAYMENT_PENDING = 'PAYMENT_PENDING',
  CONFIRMED = 'CONFIRMED',
  UPCOMING = 'UPCOMING',
  IN_PROGRESS = 'IN_PROGRESS',
  WAITING_FOR_TRAVELER_CONFIRMATION = 'WAITING_FOR_TRAVELER_CONFIRMATION',
  COMPLETED = 'COMPLETED',
  DISPUTED = 'DISPUTED',
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  /** Payment hold lapsed before the traveler paid; the time slot was released. */
  EXPIRED = 'EXPIRED',
}

export enum PaymentStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  REFUNDED = 'REFUNDED',
  PARTIALLY_REFUNDED = 'PARTIALLY_REFUNDED',
}

export enum NotificationStatus {
  UNREAD = 'unread',
  READ = 'read',
  ARCHIVED = 'archived',
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}

/** The three authoritative lifecycle states. */
export interface GuideLifecycleSummary {
  verificationStatus: VerificationStatus;
  profileStatus: ProfileStatus;
  marketplaceStatus: MarketplaceStatus;
}

export interface GuideProfileSummary {
  /** @deprecated Legacy mirror; read `lifecycle` instead. */
  verificationStatus: GuideStatus;
  /** @deprecated Legacy mirror; read `lifecycle` instead. */
  onboardingStep: number;
  isVisible: boolean;
  lifecycle?: GuideLifecycleSummary;
  adminNotes?: string;
}

/** Response of `GET /guide-profile/status`. Drives every lifecycle screen. */
export interface GuideLifecycleSnapshot extends GuideLifecycleSummary {
  hasApplication: boolean;
  canCompleteProfile: boolean;
  canSubmit: boolean;
  missingFields: string[];
  /** Version of the guide agreement the submit call must accept. */
  agreementVersion: string;
  /** Public path of that PDF, for example `/legal/SLA.pdf`. */
  agreementUrl: string;
  /** True when this profile already accepted `agreementVersion`. */
  agreementAccepted: boolean;
  adminNotes?: string;
  submittedAt?: string;
  isVisible: boolean;
}

export interface GuideApplicationInput {
  fullName: string;
  email: string;
  phone: string;
  state: string;
  city: string;
  currentlyWorkingAsGuide: boolean;
  guideExperienceRange: GuideExperienceRange;
  /** Referrer's Guide ID; the server only accepts it for a brand-new account. */
  referralCode?: string;
  referralSource?: 'LINK' | 'MANUAL';
}

export interface ReferralCodeCheck {
  valid: boolean;
  code: string | null;
  referrerFirstName: string | null;
  message?: string;
}

export interface GuideApplicationResult {
  status: 'created' | 'already_applied';
}

export interface User {
  id: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  authProvider?: AuthProvider;
  avatar?: string;
  country?: string;
  phoneVerified?: boolean;
  emailVerified?: boolean;
  isActive?: boolean;
  profileCompleted?: boolean;
  freeConnectionsRemaining?: number;
  displayName?: string;
  contactPhone?: string;
  contactConfirmedAt?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
  guideProfile?: GuideProfileSummary;
  createdAt?: string;
  lastLoginAt?: string;
  /** @deprecated use guideProfile.verificationStatus */
  guideStatus?: GuideStatus;
  /** @deprecated use guideProfile.onboardingStep */
  onboardingStep?: number;
}

export type AccountKind =
  | 'super_admin'
  | 'approved_guide'
  | 'guide_applicant'
  | 'traveler';

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
  sessionId: string;
  permissions: Permission[];
  isNewUser?: boolean;
  accountKind?: AccountKind;
  postAuthRoute?: string;
  requiresProfileSetup?: boolean;
}

export interface MeResponse {
  user: User;
  permissions: Permission[];
  accountKind: AccountKind;
  postAuthRoute: string;
  requiresProfileSetup: boolean;
}

export interface GuideProfile {
  _id: string;
  userId: string;
  /** @deprecated Legacy mirror of the lifecycle fields. */
  status: GuideStatus;
  /** @deprecated Legacy mirror of the lifecycle fields. */
  onboardingStep: number;
  verificationStatus?: VerificationStatus;
  /** Who carried out the background check. */
  verificationProvider?: VerificationProvider;
  /** The provider's candidate id, e.g. a SpringVerify reference. */
  verificationReference?: string;
  /** When the applicant's details were handed to the provider. */
  verificationSentAt?: string;
  verificationDecidedAt?: string;
  profileStatus?: ProfileStatus;
  marketplaceStatus?: MarketplaceStatus;
  isVisible?: boolean;
  activatedAt?: string;

  fullName?: string;
  bio?: string;
  profilePhoto?: string;
  bannerPhoto?: string;
  yearsOfExperience?: number;
  languagesSpoken?: string[];
  state?: string;
  city?: string;
  destinationsServed?: string[];
  specializations?: string[];

  /** Answered on the application. Absent on profiles that applied before. */
  currentlyWorkingAsGuide?: boolean;
  guideExperienceRange?: GuideExperienceRange;

  documents?: GuideDocument[];

  registeredPhone?: string;
  whatsappNumber?: string;
  instagramUrl?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;

  /** Guide agreement version accepted at submit, for example `sla-v1`. */
  agreementVersion?: string;
  agreementAcceptedAt?: string;

  adminNotes?: string;
  submittedAt?: string;
  reviewedAt?: string;
  rating?: number;
  reviewCount?: number;
  pricing?: GuidePricing | null;
  /** Set when guides are listed with a travelDate search param */
  availableOnTravelDate?: boolean;
  identity?: GuideIdentitySummary | null;
  createdAt: string;
  updatedAt: string;
}

/** Admin curation state of a guide's identity card. `legacy` = issued before publishing existed (live). */
export type IdentityCardState = 'legacy' | 'unpublished' | 'published' | 'changes_pending';

export interface GuideIdentitySummary {
  guideId: string;
  code: string;
  status: GuideIdentityStatus;
  issuedAt: string;
  activatedAt?: string;
  suspendedAt?: string;
  revokedAt?: string;
  identityUrl?: string;
  cardState?: IdentityCardState;
  cardPublishedAt?: string;
}

export interface PublicGuideIdentityPayload {
  identity: {
    guideId: string;
    code: string;
    status: GuideIdentityStatus;
    issuedAt: string;
  };
  guide: {
    name: string;
    photo?: string;
    /** Curated "City, State" from the published card. */
    location?: string;
    /** @deprecated Older API responses; `location` supersedes them. */
    city?: string;
    state?: string;
    destinations: string[];
    languages: string[];
    yearsOfExperience?: number;
    guideSince?: string;
    rating?: number;
    reviewCount?: number;
  } | null;
  profileUrl: string | null;
  identityUrl: string;
}

export interface GuideOwnIdentityPayload {
  identity: GuideIdentitySummary;
  identityUrl: string;
  profileUrl: string;
  /** False until an admin publishes the card. */
  published: boolean;
  cardState: IdentityCardState;
  /** The published card; null while unpublished. */
  guide: {
    name: string;
    photo?: string;
    location?: string;
    destinations: string[];
    languages: string[];
    yearsOfExperience?: number;
  } | null;
}

export interface IdentityCardCrop {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface IdentityCardPhoto {
  sourceUrl: string;
  sourcePublicId?: string;
  crop?: IdentityCardCrop;
  /** Server-derived rendered URL (cropped Cloudinary delivery URL). */
  url: string;
  uploadedByAdmin: boolean;
}

export interface IdentityCardContent {
  name: string;
  location?: string;
  languages: string[];
  destinations: string[];
  photo: IdentityCardPhoto | null;
}

export interface AdminIdentityCard {
  identity: { guideId: string; code: string; status: GuideIdentityStatus };
  identityUrl: string;
  state: IdentityCardState;
  draft: IdentityCardContent;
  hasSavedDraft: boolean;
  published: IdentityCardContent | null;
  seed: IdentityCardContent;
  profilePhoto: { url: string; publicId?: string; croppable: boolean } | null;
  draftUpdatedAt: string | null;
  publishedAt: string | null;
  canPublish: boolean;
}

export interface IdentityCardDraftInput {
  name: string;
  location?: string;
  languages: string[];
  destinations: string[];
  photo: { sourceUrl: string; sourcePublicId?: string; crop?: IdentityCardCrop } | null;
}

export interface GuideDocument {
  _id: string;
  guideId: string;
  type: DocumentType;
  url: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  isVerified: boolean;
  uploadedAt: string;
}

export interface OnboardingProgress {
  profile: GuideProfile;
  documents: GuideDocument[];
  completion?: OnboardingCompletion;
}

export type OnboardingNextAction =
  | 'PROFILE_ACCOUNT'
  | 'PROFILE_NAME'
  | 'PROFILE_PHOTO'
  | 'PROFILE_EXPERIENCE'
  | 'PROFILE_LANGUAGES'
  | 'PROFILE_LOCATION'
  | 'PROFILE_DESTINATIONS'
  | 'PROFILE_SPECIALTIES'
  | 'PROFILE_BIO'
  | 'PROFILE_READY'
  | 'VERIFY_INTRO'
  | 'VERIFY_SELFIE'
  | 'VERIFY_AADHAAR_FRONT'
  | 'VERIFY_AADHAAR_BACK'
  | 'CONTACT_WHATSAPP'
  | 'REVIEW'
  | 'NONE';

export interface OnboardingCompletion {
  profileComplete: boolean;
  verificationComplete: boolean;
  contactComplete: boolean;
  canSubmit: boolean;
  nextAction: OnboardingNextAction;
}

export interface OnboardingStep1Data {
  fullName: string;
  bio?: string;
  profilePhoto: string;
  yearsOfExperience: number;
  languagesSpoken: string[];
  state: string;
  city: string;
  destinationsServed: string[];
  specializations: string[];
  email?: string;
  loginPhone?: string;
}

export interface OnboardingStep2Data {
  selfieUrl: string;
  aadhaarFrontUrl: string;
  aadhaarBackUrl: string;
  certificateUrls?: string[];
}

export interface OnboardingStep3Data {
  whatsappNumber: string;
  instagramUrl?: string;
  facebookUrl?: string;
  linkedinUrl?: string;
  youtubeUrl?: string;
  emergencyContactName?: string;
  emergencyContactNumber?: string;
}

export interface UploadResponse {
  documentId: string;
  url: string;
  type: DocumentType;
  originalName: string;
}

export interface GuideVerificationHistory {
  _id: string;
  guideId: string;
  adminId: { _id: string; firstName?: string; lastName?: string; email?: string };
  action: GuideVerificationAction;
  notes: string;
  previousStatus: GuideStatus;
  newStatus: GuideStatus;
  createdAt: string;
}

export interface AdminGuideReview {
  profile: GuideProfile & { userId: User };
  documents: GuideDocument[];
  history: GuideVerificationHistory[];
}

export interface GuideRequestUserSummary {
  id: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  email?: string;
  phone?: string;
}

export interface GuideRequestDestinationSummary {
  id: string;
  name: string;
  country?: string;
  region?: string;
}

export interface GuideRequest {
  id: string;
  traveler: GuideRequestUserSummary;
  guide: GuideRequestUserSummary;
  destination: GuideRequestDestinationSummary;
  message: string;
  travelDate?: string;
  travelerCount: number;
  status: GuideRequestStatus | string;
  guideResponseMessage?: string;
  contactUnlocked: boolean;
  travelerContact?: { email?: string; phone?: string };
  approvedAt?: string;
  rejectedAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated use GuideRequest */
export interface LegacyGuideRequest {
  _id: string;
  travelerId: string | User;
  guideId: string | User;
  destinationId: string | Destination;
  message: string;
  requestedDate?: string;
  guideResponseMessage?: string;
  status: string;
  contactUnlockedAt?: string;
  consumesConnection: boolean;
  createdAt: string;
}

export interface GuidePricing {
  guideId: string;
  hourlyPrice: number | null;
  fullDayPrice: number | null;
  currency: string;
  updatedAt?: string;
}

export interface GuidePricingHistoryEntry {
  id: string;
  hourlyPrice: number;
  fullDayPrice: number;
  currency: string;
  changedAt: string;
}

export interface GuidePricingHistoryResponse {
  items: GuidePricingHistoryEntry[];
  total: number;
  page: number;
  limit: number;
}

export interface GuideAvailability {
  id: string;
  guideId: string;
  date: string;
  status: GuideAvailabilityStatus;
  source: GuideAvailabilitySource;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

/** Server: modules/bookings/slot-claims.ts `DaySchedule`. Times are IST "HH:mm". */
export type GuideDayStatus = 'free' | 'partial' | 'full' | 'vacation' | 'past';

export interface GuideDaySchedule {
  date: string;
  status: GuideDayStatus;
  /** Booked ranges including the post-tour buffer. */
  busy: Array<{ from: string; to: string }>;
  /** Today only: earliest start still in the future. */
  earliestStart?: string;
}

export interface GuideSchedule {
  timezone: string;
  workingHours: { start: string; end: string };
  slotMinutes: number;
  bufferMinutes: number;
  maxFullDayDays: number;
  days: GuideDaySchedule[];
}

export interface TravelerContactSnapshot {
  name: string;
  phone: string;
}

export interface BookingGuideSummary {
  fullName: string;
  profilePhoto?: string;
  /** GuideProfile._id — use for /guides/:id links (booking.guideId is the user id) */
  profileId?: string;
  phone?: string;
  email?: string;
  whatsappNumber?: string;
}

/** Guide ↔ traveler contacts unlock 24h before the trip (server: contact-access.ts). */
export type ContactAccessState = 'locked' | 'unlocked' | 'unavailable';

export interface BookingContactAccess {
  state: ContactAccessState;
  /** ISO time the contacts unlock; present only while `locked`. */
  unlocksAt?: string;
}

export interface Booking {
  id: string;
  travelerId: string;
  guideId: string;
  guideRequestId?: string;
  destinationId: string;
  destinationName?: string;
  destinationLabel?: string;
  pricingType: BookingPricingType;
  duration: number;
  travelerCount: number;
  grossAmount: number;
  currency: string;
  status: BookingStatus;
  travelDate: string;
  /** Exact trip window (UTC ISO). Absent only on very old bookings. */
  startAt?: string;
  endAt?: string;
  /** End before paid extra time; absent when the trip was never extended. */
  originalEndAt?: string;
  /** Whole hours added by paid extensions. */
  extendedHours?: number;
  timezone?: string;
  /** Unpaid bookings expire at this time. */
  holdExpiresAt?: string;
  notes?: string;
  /** @deprecated No longer returned by the API — use `travelerName` / `travelerContact`. */
  travelerContactSnapshot?: TravelerContactSnapshot;
  /** Present only once contacts unlock (guide/admin view). */
  travelerContact?: TravelerContactSnapshot;
  travelerName?: string;
  contactAccess?: BookingContactAccess;
  guide?: BookingGuideSummary;
  confirmedAt?: string;
  upcomingAt?: string;
  inProgressAt?: string;
  completionRequestedAt?: string;
  travelerConfirmDeadline?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelReason?: string;
  cancelPolicyVersion?: number;
  cancelRefundPercent?: number;
  cancelRefundAmount?: number;
  cancelPolicyTier?: string;
  disputedAt?: string;
  disputeReason?: string;
  disputeResolution?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BookingQuote {
  grossAmount: number;
  currency: string;
  pricingType: BookingPricingType;
  duration: number;
  travelerCount: number;
}

export type PaymentPurpose = 'booking' | 'trip_extension';

export interface Payment {
  id: string;
  bookingId: string;
  purpose?: PaymentPurpose;
  extensionId?: string;
  travelerId: string;
  guideId: string;
  provider: string;
  providerPaymentId?: string;
  providerOrderId: string;
  amount: number;
  currency: string;
  paymentStatus: PaymentStatus;
  createdAt: string;
  updatedAt: string;
}

// ─── Admin booking oversight ────────────────────────────────────────────────

export type AdminBookingAttentionReason =
  | 'disputed'
  | 'confirmation_overdue'
  | 'stuck_payment_pending'
  | 'start_overdue'
  | 'paid_but_cancelled';

export interface AdminBookingRow {
  id: string;
  status: BookingStatus;
  travelDate: string;
  startAt?: string;
  endAt?: string;
  createdAt: string;
  destinationLabel?: string;
  pricingType: BookingPricingType;
  duration: number;
  travelerCount: number;
  grossAmount: number;
  currency: string;
  traveler: { id: string; name?: string; email?: string; phone?: string };
  guide: { userId: string; profileId?: string; fullName?: string; photo?: string };
  payment?: { id: string; status: PaymentStatus; provider: string; refundedAmount: number };
  cancelRefundAmount?: number;
  cancelPolicyTier?: string;
  disputeReason?: string;
  attentionReasons: AdminBookingAttentionReason[];
}

export interface AdminBookingStats {
  byStatus: Record<BookingStatus, number>;
  total: number;
  todayCreated: number;
  monthConfirmedCount: number;
  gmvThisMonth: number;
  needsAttention: number;
  attentionByReason: Record<AdminBookingAttentionReason, number>;
}

export interface BookingStatusHistoryEntry {
  from: BookingStatus;
  to: BookingStatus;
  at: string;
  actorId?: string;
  actorRole?: string;
  actorName?: string;
  reason?: string;
}

export interface AdminBookingDetail extends Omit<Booking, 'guide'> {
  disputeResolvedAt?: string;
  disputeResolvedBy?: string;
  disputeResolvedByName?: string;
  statusHistory: BookingStatusHistoryEntry[];
  traveler: {
    id: string;
    name?: string;
    email?: string;
    phone?: string;
    contactPhone?: string;
    contactSnapshot?: TravelerContactSnapshot;
  };
  guide: {
    userId: string;
    profileId?: string;
    fullName?: string;
    photo?: string;
    email?: string;
    phone?: string;
    whatsappNumber?: string;
  };
  payments: {
    id: string;
    purpose?: PaymentPurpose;
    extensionId?: string;
    provider: string;
    providerOrderId: string;
    providerPaymentId?: string;
    amount: number;
    currency: string;
    status: PaymentStatus;
    createdAt: string;
    updatedAt: string;
    lastFailedAttemptAt?: string;
  }[];
  refunds: {
    id: string;
    paymentId: string;
    amount: number;
    currency: string;
    reason?: string;
    /** SUBMITTED until per-refund status tracking exists. */
    status: string;
    createdAt?: string;
  }[];
  settlement?: {
    id: string;
    grossAmount: number;
    commissionType: string;
    commissionValue: number;
    platformAmount: number;
    guideAmount: number;
    reversedGuideAmount?: number;
    currency: string;
    status: string;
    createdAt: string;
  };
  extensions?: {
    id: string;
    requestedBy: 'traveler' | 'guide';
    status: TripExtensionStatus;
    hours: number;
    fromEndAt: string;
    toEndAt: string;
    hourlyRate: number;
    totalAmount: number;
    currency: string;
    expiresAt: string;
    paidAt?: string;
    paymentId?: string;
    createdAt: string;
  }[];
  extensionSettlements?: {
    id: string;
    extensionId?: string;
    grossAmount: number;
    platformAmount: number;
    guideAmount: number;
    reversedGuideAmount?: number;
    currency: string;
    status: string;
    createdAt: string;
  }[];
  auditTrail: {
    id: string;
    action: string;
    actorId?: string;
    actorName?: string;
    targetEntityType?: string;
    metadata?: Record<string, unknown>;
    createdAt: string;
  }[];
  cancellationQuote?: {
    allowed: boolean;
    refundPercent: number;
    refundAmount: number;
    tier: string;
    message: string;
    hoursUntilTravel: number;
  };
  attentionReasons: AdminBookingAttentionReason[];
}

export type TripExtensionStatus =
  | 'REQUESTED'
  | 'ACCEPTED'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'DECLINED'
  | 'EXPIRED'
  | 'PAYMENT_FAILED'
  | 'CANCELLED';

/** Extra whole hours added to the end of a booked trip, paid separately. */
export interface TripExtension {
  id: string;
  bookingId: string;
  requestedBy: 'traveler' | 'guide';
  status: TripExtensionStatus;
  /** Who has to act next while the request is open. */
  awaiting: 'traveler' | 'guide' | null;
  isOpen: boolean;
  fromEndAt: string;
  toEndAt: string;
  hours: number;
  hourlyRate: number;
  travelerCount: number;
  totalAmount: number;
  currency: string;
  /** Guide view only. */
  estimatedGuideAmount?: number;
  /** Request, approval and payment must all finish by this time. */
  expiresAt: string;
  respondedAt?: string;
  paidAt?: string;
  createdAt: string;
  updatedAt: string;
  /** Server clock when the response was built, for countdowns. */
  serverNow: string;
}

export interface GuideTripExtension extends TripExtension {
  booking: {
    id: string;
    travelerName?: string;
    destinationLabel?: string;
    startAt?: string;
    endAt?: string;
    travelerCount: number;
    status: BookingStatus;
  } | null;
}

export interface TripExtensionOption {
  hours: number;
  newEndAt: string;
  totalAmount: number;
  /** Guide view only. */
  estimatedGuideAmount?: number;
}

export interface TripExtensionOptions {
  bookingId: string;
  eligible: boolean;
  reason?: string;
  currentEndAt?: string;
  originalEndAt?: string;
  extendedHours: number;
  currency: string;
  travelerCount: number;
  hourlyRate: number | null;
  options: TripExtensionOption[];
  openExtension: TripExtension | null;
  serverNow: string;
}

export interface TripExtensionCheckoutResponse extends InitiatePaymentResponse {
  extension: TripExtension;
}

export interface InitiatePaymentResponse {
  payment: Payment;
  paymentSessionId?: string;
  checkoutUrl?: string;
  providerOrderId: string;
  alreadyPaid?: boolean;
}

export interface ReviewTravelerSummary {
  id: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

export interface ReviewDestinationSummary {
  id: string;
  name: string;
}

export interface Review {
  id: string;
  traveler: ReviewTravelerSummary;
  rating: number;
  comment: string;
  destination?: ReviewDestinationSummary;
  createdAt: string;
}

export interface ReviewSummary {
  averageRating: number;
  reviewCount: number;
  breakdown: Record<string, number>;
}

export interface GuideReviewsResponse {
  summary: ReviewSummary;
  data: Review[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Finance ────────────────────────────────────────────────────────────────

export interface GuideWallet {
  id: string;
  guideId: string;
  pendingBalance: number;
  availableBalance: number;
  withdrawnBalance: number;
  totalLifetimeEarnings: number;
  currency: string;
  updatedAt: string;
}

export interface WalletTransaction {
  id: string;
  walletId: string;
  guideId: string;
  bookingId?: string;
  amount: number;
  transactionType: string;
  referenceType: string;
  referenceId: string;
  status: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface GuideBankAccount {
  id: string;
  guideId: string;
  accountHolderName: string;
  accountNumberMasked: string;
  ifscCode: string;
  bankName: string;
  branch?: string;
  upiId?: string;
  isPrimary: boolean;
  verificationStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface WithdrawalRequest {
  id: string;
  guideId: string;
  walletId: string;
  bankAccountId: string;
  amount: number;
  currency: string;
  status: string;
  remarks?: string;
  adminRemarks?: string;
  requestedAt: string;
  approvedAt?: string;
  processedAt?: string;
  approvedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CommercialAgreement {
  id: string;
  guideId: string;
  commissionType: string;
  commissionValue: number;
  effectiveFrom: string;
  effectiveTo?: string;
  status: string;
  createdBy: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WalletSummary {
  wallet: GuideWallet;
  commercial: {
    id: string;
    guideId: string;
    commissionType: string;
    commissionValue: number;
    effectiveFrom: string;
    effectiveTo?: string;
  };
}

export interface Settlement {
  id: string;
  bookingId: string;
  paymentId: string;
  guideId: string;
  grossAmount: number;
  commissionType: string;
  commissionValue: number;
  platformAmount: number;
  guideAmount: number;
  currency: string;
  settlementStatus: string;
  createdAt: string;
  updatedAt: string;
}

export interface FinanceReportSummary {
  totalPending: number;
  totalAvailable: number;
  totalWithdrawn: number;
  totalEarnings: number;
  pendingWithdrawals: number;
}

export interface FinanceAnalytics {
  summary: FinanceReportSummary;
  settlementSeries: Array<{
    date: string;
    grossAmount: number;
    guideAmount: number;
    platformAmount: number;
    count: number;
  }>;
  withdrawalStatusBreakdown: Array<{
    status: string;
    count: number;
    amount: number;
  }>;
  settlementTotals: {
    totalGross: number;
    totalGuide: number;
    totalPlatform: number;
    count: number;
  };
  days: number;
}

export interface Destination {
  _id: string;
  name: string;
  country: string;
  region?: string;
  description?: string;
  imageUrl?: string;
  imagePublicId?: string;
  highlight?: string;
  isActive: boolean;
  isLive?: boolean;
  sortOrder?: number;
}

/** A city the admin has put live on the home page. `region` is the state. */
export interface LiveCity {
  _id: string;
  name: string;
  region?: string;
  highlight?: string;
  imageUrl?: string;
}

export interface Notification {
  _id: string;
  userId: string;
  title: string;
  message: string;
  status: NotificationStatus;
  type?: string;
  data?: Record<string, unknown>;
  createdAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}
